/**
 * Parser : Proposal JSON → Web Data (Puck-compatible)
 *
 * Transforme la structure de l'éditeur (containers/columns/boxes)
 * en un objet multi-pages prêt pour Puck.
 */

import { buildSkeletonFromTemplate } from '../puck/skeletonSeeds';
import { extractProposalRichTextPalette } from '../utils/proposalRichTextPalette';
import { zoneKeyForSectionColumn } from '../utils/proposalSectionColumns';
import { resolveContainerLayout } from '../utils/proposalNestedLayout';
import {
  computeContainerAllSpacingPx,
  computeContainerTrailingSpacingPx,
  computeSectionMarginBottomCss,
  isSpacingOnlyContainer,
} from '../utils/proposalSectionSpacing';
import { proposalPxToSpacingCss } from '../utils/proposalUnits';
import { resolveBoxPositions } from '../utils/proposalBoxAlign';
import {
  computeProposalContentWidthPx,
} from '../utils/proposalBoxLayout';
import { buildWebTextStyle, buildWebTitleStyle } from '../utils/proposalTextStyle';
import { getStaticPageOrder } from '../utils/staticPageOrder';
import { filterChapterSeparationContainers } from '../../utils/chapterSeparation';
import { proposalTableDesign } from '../../shared/tables/proposalTableStyle';
import { extractProposalStyles } from '../../shared/theme/themeTypography';
import { DEFAULT_SITE_FONT } from '../../shared/theme/themeConfiguration';
import { renderVariableHtml } from '../utils/variableNodes';
import { escapeHtml } from '../utils/draftRawToHtml';
import { ItemTypes } from '../../constants/constants';
import { imageFitWebProps } from '../../shared/boxes/imageFit';
import {
  accordionPanelPropsFromBox,
  isEmptyAccordionPanelProps,
} from '../../shared/boxes/accordionPanel/accordionPanelFromBox';
import {
  infoCardPropsFromBox,
  isEmptyInfoCardProps,
} from '../../shared/boxes/infoCard/infoCardFromBox';
import { convertFromRaw } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import { toPuckTableConfig } from '../utils/puckTableConfig';

// ─── Constantes ───────────────────────────────────────────────────────────────

const PAGE_WIDTH_PX = 825; // Largeur de référence du canvas proposal
const TITLE_TYPES = ['title1', 'title2', 'title3', 'title4'];
const IGNORED_TYPES = new Set(['space', 'line', 'line_break', 'group_boxes', 'summary', 'mask', 'section']);

/**
 * Types que le Proposal rend avec le même composant que leur jumeau non
 * variable (`common/Box.jsx`), et qui doivent donc produire le même bloc web.
 * Sans ça ils tombaient dans le fall-through et disparaissaient du site.
 */
const TEXT_TYPES = new Set(['text', 'text_variable']);
const IMAGE_TYPES = new Set(['img', 'img_variable']);
const TABLE_TYPES = new Set(['table', 'table_variable']);
const CHECKBOX_TYPES = new Set(['checkboxs_variable', 'question']);

/**
 * Box variables du Proposal (texte, image, tableau) : elles portent leur réglage de
 * formulaire, et restent sur le site même masquées (`hideField`) — le bloc
 * masqué se réaffiche depuis l'éditeur, comme la box depuis le formulaire.
 */
const VARIABLE_BOX_TYPES = new Set(['text_variable', 'img_variable', 'table_variable']);

const dropsHiddenBox = (box) => !!box.hideField && !VARIABLE_BOX_TYPES.has(box.type);

const plainDraft = (value) => (typeof value === 'string' ? draftToPlainText(value) : '');

// Réglages rangés comme le site les lit : à côté de l'URL pour l'image
// (`imageSlots`), dans `form` pour le texte et le tableau (`siteVariableBoxes`).
// `toHtml` convertit les textes proposés (Draft) comme le contenu de la box.
function variableBoxFormProps(box, toHtml) {
  if (!VARIABLE_BOX_TYPES.has(box.type)) return {};
  const settings = {
    showInForm: !box.hideInForm,
    name: plainDraft(box.name),
    remark: plainDraft(box.remark),
  };
  const visibility = box.hideField ? { isVisible: false } : {};
  if (box.type === 'table_variable') return { form: settings, ...visibility };
  if (box.type === 'text_variable') {
    return {
      form: {
        ...settings,
        valueRequired: !!box.value_required,
        predefinedValues: (box.predefinedValues || []).filter((value) => plainDraft(value).trim()).map(toHtml),
      },
      ...visibility,
    };
  }
  return {
    srcForm: {
      ...settings,
      predefinedValues: (box.predefinedValues || [])
        .filter((value) => value?.url)
        .map(({ url, mediaId, resolution }) => ({ url, mediaId: mediaId ?? null, resolution })),
    },
    ...visibility,
  };
}

/** Rien de masqué : défaut inerte, pour que le parser reste appelable seul. */
const EMPTY_HIDDEN_TYPES = new Set();
const URL_PATTERN = /^https?:\/\//i;

// ─── 5.1 Normalisation px → 12 colonnes ──────────────────────────────────────

export function pxToSpan(widthPx, totalWidth = PAGE_WIDTH_PX) {
  if (!widthPx || typeof widthPx !== 'number') return 12;
  if (widthPx > totalWidth * 0.9) return 12;
  return Math.max(1, Math.round((widthPx / totalWidth) * 12));
}

// ─── 5.2 Conversion Draft.js → HTML ──────────────────────────────────────────

/**
 * Convertit un `content.raw` Draft.js (string JSON) en HTML.
 * Supporte les variables VARIABLE entity → remplacées dans la chaîne HTML finale.
 */
export function draftToHtml(rawJsonString, inlineStylesConfig = {}) {
  if (!rawJsonString) return '';
  try {
    const raw = JSON.parse(rawJsonString);
    const contentState = convertFromRaw(raw);

    const options = {
      inlineStyles: {
        BOLD: { element: 'strong' },
        ITALIC: { element: 'em' },
        UNDERLINE: { element: 'u' },
        STRIKETHROUGH: { element: 's' },
        ...buildInlineStylesOptions(inlineStylesConfig),
      },
      entityStyleFn: (entity) => {
        const type = entity.get('type');
        if (type === 'VARIABLE') {
          const { key, lineStyle, separator } = entity.getData() ?? {};
          return {
            element: 'span',
            style: {},
            // Nombre de lignes et séparateur d'une adresse : réglés sur
            // l'occurrence, ils la suivent dans le site.
            attributes: {
              'data-variable': key || '',
              ...(lineStyle ? { 'data-variable-line-style': lineStyle } : {}),
              ...(separator ? { 'data-variable-separator': separator } : {}),
            },
          };
        }
        if (type === 'LINK') {
          return {
            element: 'a',
            attributes: { href: entity.getData()?.url || '#', target: '_blank', rel: 'noopener noreferrer' },
          };
        }
        return null;
      },
    };

    return stateToHTML(contentState, options);
  } catch {
    // Fallback : retourner le texte brut extrait du JSON si possible
    try {
      const raw = JSON.parse(rawJsonString);
      return raw.blocks?.map((b) => b.text).join('\n') || rawJsonString;
    } catch {
      return rawJsonString;
    }
  }
}

/**
 * Extrait le texte brut depuis un raw Draft.js (pour les slugs, titres, etc.)
 */
export function draftToPlainText(rawJsonString) {
  if (!rawJsonString) return '';
  try {
    const raw = JSON.parse(rawJsonString);
    return raw.blocks?.map((b) => b.text).join(' ').trim() || '';
  } catch {
    return rawJsonString;
  }
}

/**
 * Convertit les inlineStyles de la configuration (ex: "color_xxx": { style: "rgba(...)" })
 * en options compatibles draft-js-export-html.
 */
function buildInlineStylesOptions(inlineStylesConfig) {
  if (!inlineStylesConfig || typeof inlineStylesConfig !== 'object') return {};

  // Le JSON proposal n'a pas toujours `configuration.inlineStyles`.
  // On reconstruit alors une map compatible Draft à partir de la config globale.
  const syntheticInlineStyles = {};

  // Couleurs nommées utilisables dans les inlineStyleRanges (ex: color_xxx, white, black)
  if (Array.isArray(inlineStylesConfig.colors)) {
    inlineStylesConfig.colors.forEach((c) => {
      if (c?.label && c?.style) syntheticInlineStyles[c.label] = c;
    });
  }

  // Familles de polices inline (ex: FONT_FAMILY_Anton)
  if (Array.isArray(inlineStylesConfig.fontFamilyList)) {
    inlineStylesConfig.fontFamilyList.forEach((f) => {
      const key = f?.key;
      const familyName = f?.familyName;
      if (key && familyName) {
        syntheticInlineStyles[`FONT_FAMILY_${String(key).replace(/\s+/g, '_')}`] = {
          familyName,
        };
      }
    });
  }

  // Tailles inline usuelles (l'éditeur produit des styles FONT_SIZE_xx)
  for (let size = 8; size <= 72; size += 1) {
    syntheticInlineStyles[`FONT_SIZE_${size}`] = { size };
  }

  const mergedInlineStyles = {
    ...syntheticInlineStyles,
    ...inlineStylesConfig,
  };

  const result = {};
  Object.entries(mergedInlineStyles).forEach(([key, value]) => {
    if (key.startsWith('FONT_SIZE_')) {
      const size = key.replace('FONT_SIZE_', '');
      result[key] = { style: { fontSize: `${size}px` } };
    } else if (key.startsWith('FONT_FAMILY_')) {
      const family = value?.familyName || key.replace('FONT_FAMILY_', '').replace(/_/g, ' ').trim();
      if (family) {
        result[key] = { style: { fontFamily: `${family}, sans-serif` } };
      }
    } else if (key.startsWith('color_') && value?.style) {
      result[key] = { style: { color: value.style } };
    } else if (key === 'white') {
      result[key] = { style: { color: '#ffffff' } };
    } else if (key === 'black') {
      result[key] = { style: { color: '#000000' } };
    }
  });
  return result;
}

// ─── 5.3 Résolution des variables ────────────────────────────────────────────

/**
 * Remplace les {{key}} par les valeurs résolues.
 * Les variables inconnues sont conservées telles quelles.
 * `asHtml` : la valeur, une donnée métier, est insérée comme texte dans du HTML.
 */
export function resolveVariables(html, variables = {}, { asHtml = false } = {}) {
  if (!html || !variables || Object.keys(variables).length === 0) return html;
  return html.replace(/\{\{([^}]+)\}\}/g, (match, key) => {
    const value = variables[key.trim()];
    if (value == null) return match;
    return asHtml ? escapeHtml(value) : value;
  });
}

/**
 * Convertit les entités Draft.js VARIABLE (`data-variable="key"`) en nœuds
 * variable de l'éditeur web.
 *
 * Elles étaient auparavant aplaties en texte : la clé disparaissait, et
 * « Rafraîchir les variables » n'avait plus rien à mettre à jour dans le
 * contenu venu du proposal. Le nœud produit garde la clé ; son nom et sa valeur
 * définitifs sont écrits juste après par `resolveVariableNodesDeep`
 * (`buildWebDataForProposal`), qui les tient du catalogue.
 */
const VARIABLE_SPAN_OPEN = /<span\b[^>]*data-variable="([^"]*)"[^>]*>/g;
const SPAN_TAG = /<\/?span\b[^>]*>/g;

/**
 * Fin du `</span>` qui ferme l'entité, `<span>` imbriqués compris : Draft rend
 * les styles inline *à l'intérieur* de l'entité
 * (`<span data-variable><span style="font-size: 16px">…</span></span>`), donc
 * un `.*?</span>` s'arrête au mauvais endroit et emporte du balisage dans le nom.
 */
function findSpanEnd(html, from) {
  SPAN_TAG.lastIndex = from;
  let depth = 1;
  let match = SPAN_TAG.exec(html);
  while (match) {
    depth += match[0].startsWith('</') ? -1 : 1;
    if (depth === 0) return { contentEnd: match.index, end: SPAN_TAG.lastIndex };
    match = SPAN_TAG.exec(html);
  }
  return null;
}

/** Un attribut de la balise ouvrante d'une entité, entités HTML décodées. */
function openTagAttribute(openTag, name) {
  const doc = new DOMParser().parseFromString(`${openTag}</span>`, 'text/html');
  return doc.body.firstElementChild?.getAttribute(name) || null;
}

/** Texte lisible d'un fragment HTML (balises retirées, entités décodées). */
function htmlToText(fragment) {
  const doc = new DOMParser().parseFromString(fragment, 'text/html');
  return (doc.body.textContent || '').trim();
}

/**
 * Le nœud variable remplace le texte de l'entité, mais garde autour de lui les
 * balises de style que Draft y a imbriquées : Tiptap en fait les marques du
 * nœud, et la variable garde la police, la taille et le gras de la phrase.
 */
function variableWithSurroundingStyle(content, variableHtml) {
  let out = '';
  let placed = false;
  Array.from(content.matchAll(/<[^>]+>|[^<]+/g)).forEach(([chunk]) => {
    if (chunk.startsWith('<')) {
      out += chunk;
      return;
    }
    if (!placed) {
      out += variableHtml;
      placed = true;
    }
  });
  return placed ? out : variableHtml;
}

export function resolveVariablesInHtml(html, variables = {}) {
  if (!html) return html;
  const source = resolveVariables(html, variables, { asHtml: true });

  let result = '';
  let cursor = 0;
  VARIABLE_SPAN_OPEN.lastIndex = 0;
  let open = VARIABLE_SPAN_OPEN.exec(source);
  while (open) {
    const contentStart = open.index + open[0].length;
    const span = findSpanEnd(source, contentStart);
    if (!span) break;

    const key = open[1];
    const content = source.slice(contentStart, span.contentEnd);
    const variableHtml = renderVariableHtml({
      key,
      // Le texte rendu par Draft sert de nom de repli : si la clé a disparu du
      // catalogue, la variable reste lisible au lieu de devenir un trou.
      name: htmlToText(content),
      value: variables[key] ?? '',
      lineStyle: openTagAttribute(open[0], 'data-variable-line-style'),
      separator: openTagAttribute(open[0], 'data-variable-separator'),
    });

    result += source.slice(cursor, open.index);
    result += variableWithSurroundingStyle(content, variableHtml);
    cursor = span.end;
    VARIABLE_SPAN_OPEN.lastIndex = span.end;
    open = VARIABLE_SPAN_OPEN.exec(source);
  }

  return result + source.slice(cursor);
}

// ─── 5.4 Slugify & découpage en pages ────────────────────────────────────────

/**
 * Normalisation seule, sans repli : rend '' quand l'entrée ne contient rien de
 * slugifiable. C'est ce qu'il faut partout où le vide est une valeur légitime —
 * une ancre de bloc, par exemple, dont l'absence veut dire « vise-moi par mon
 * id ». `slugify` ci-dessous y ajoute le repli qui n'a de sens que pour une
 * page, dont le slug ne peut jamais être vide.
 */
export function slugifyOrEmpty(text) {
  return (text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Retire les accents
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/[\s]+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 60);
}

export function slugify(text) {
  return slugifyOrEmpty(text) || 'page';
}

/** Réserve des slugs uniques (même algorithme que parseProposalToWebData). */
export function makeReserveUniqueSlug() {
  const counts = {};
  return (baseSlug) => {
    const normalized = slugify(baseSlug || 'page');
    if (counts[normalized] == null) {
      counts[normalized] = 0;
      return normalized;
    }
    counts[normalized] += 1;
    return `${normalized}-${counts[normalized]}`;
  };
}

/**
 * Pages statiques du template dans l'ordre défini par static_page_order.
 */
function mapTemplateStaticPages(webTemplate, reserveUniqueSlug) {
  const staticPagesFromTemplate = webTemplate?.static_pages || {};
  const orderedSlugs = getStaticPageOrder(
    staticPagesFromTemplate,
    webTemplate?.static_page_order,
  );
  const entries = [];
  const resolvedSlugs = [];
  for (const fallbackSlug of orderedSlugs) {
    const page = staticPagesFromTemplate[fallbackSlug];
    if (!page || typeof page !== 'object') continue;
    const title = page.title || 'Page';
    const uniqueSlug = reserveUniqueSlug(page.slug || fallbackSlug || title);
    entries.push([
      uniqueSlug,
      {
        title,
        slug: uniqueSlug,
        type: 'static',
        puckData: page.puckData || { content: [], root: {} },
      },
    ]);
    resolvedSlugs.push(uniqueSlug);
  }
  return { staticPages: Object.fromEntries(entries), staticSlugs: resolvedSlugs };
}

/**
 * Pages statiques du template au format web_data, telles qu'un site généré
 * depuis lui les reçoit. Sert à l'aperçu d'un modèle, qui n'a pas de document.
 */
export function templateStaticPagesWebData(webTemplate) {
  const { staticPages, staticSlugs } = mapTemplateStaticPages(
    webTemplate,
    makeReserveUniqueSlug(),
  );
  return { pages: staticPages, pageOrder: staticSlugs };
}

/**
 * Slugs attribués aux pages statiques du template (aligné sur parseProposalToWebData).
 * Sert à la régénération pour ne pas réinjecter les pages « template » de l’ancien modèle.
 */
export function getSlugSetForWebTemplateStaticPages(webTemplate) {
  const reserveUniqueSlug = makeReserveUniqueSlug();
  const { staticSlugs } = mapTemplateStaticPages(webTemplate, reserveUniqueSlug);
  return new Set(staticSlugs);
}

/**
 * Découpe les containers en pages à partir des boxes title1.
 * Retourne un tableau de { title, slug, containerIds[] }.
 * Un conteneur "Accueil" est toujours créé en premier.
 */
export function splitIntoPages(boxes, containers, columns, rowContainers = []) {
  // Indexation rapide : columnId → column
  const columnById = Object.fromEntries(columns.map((c) => [c.id, c]));

  // Indexation : columnId → containerId **top-level**, sous-colonnes comprises.
  // Un titre peut vivre dans une sous-colonne : le dépôt d'un type structurel y
  // est refusé (`STRUCTURAL_BOX_TYPES`), mais découper en lignes une colonne qui
  // en portait déjà un l'y déplace (`mutateSetColumnRows`). Sans cette descente,
  // ce titre n'ouvrirait plus sa page.
  const containerByColumnId = {};
  const rowById = new Map(rowContainers.map((r) => [r.id, r]));
  const indexColumn = (colId, containerId) => {
    containerByColumnId[colId] = containerId;
    (columnById[colId]?.rowContainerIds ?? []).forEach((rowId) => {
      (rowById.get(rowId)?.columnsIds ?? []).forEach((subId) => {
        indexColumn(subId, containerId);
      });
    });
  };
  containers.forEach((container) => {
    container.columnsIds.forEach((colId) => indexColumn(colId, container.id));
  });

  // Trouver les title1 boxes et leur container, dans l'ordre des containers
  const containerOrder = containers.map((c) => c.id);

  const title1Entries = [];
  boxes.forEach((box) => {
    if (box.type !== 'title1') return;
    const containerId = containerByColumnId[box.columnId];
    if (!containerId) return;
    const col = columnById[box.columnId];
    // Inclure même les boxes en drawMode — les title1 servent à séparer les pages
    const orderIdx = containerOrder.indexOf(containerId);
    const titleText = draftToPlainText(box.content?.raw) || 'Section';
    title1Entries.push({ containerId, orderIdx, titleText, box });
  });

  // Trier par ordre dans les containers
  title1Entries.sort((a, b) => a.orderIdx - b.orderIdx);

  // Dédupliquer par containerId (un container peut avoir plusieurs title1)
  const seen = new Set();
  const uniqueTitles = title1Entries.filter(({ containerId }) => {
    if (seen.has(containerId)) return false;
    seen.add(containerId);
    return true;
  });

  // Construire les pages
  const pages = [];

  // Page Accueil : containers avant le premier title1
  const firstTitleIdx = uniqueTitles.length > 0 ? uniqueTitles[0].orderIdx : containerOrder.length;
  pages.push({
    title: 'Accueil',
    slug: 'accueil',
    type: 'proposal',
    containerIds: containerOrder.slice(0, firstTitleIdx),
  });

  // Pages issues des title1
  uniqueTitles.forEach(({ containerId, orderIdx, titleText }, i) => {
    const nextIdx = i + 1 < uniqueTitles.length ? uniqueTitles[i + 1].orderIdx : containerOrder.length;
    const slug = slugify(titleText);
    pages.push({
      title: titleText,
      slug,
      type: 'proposal',
      containerIds: containerOrder.slice(orderIdx, nextIdx),
    });
  });

  return pages;
}

// ─── 5.5 & 5.6 Transformation Box → Bloc Puck ────────────────────────────────

function isImageUrl(value) {
  return typeof value === 'string' && (URL_PATTERN.test(value) || value.startsWith('/'));
}

function isDataImageUrl(value) {
  return typeof value === 'string' && value.trim().toLowerCase().startsWith('data:image/');
}

/** Aligné sur Cell.jsx (affichage tableau) : forcer https pour les src distantes */
function normalizeTableImageSrc(url) {
  if (typeof url !== 'string') return '';
  return url.trim().replace(/^http:\/\//i, 'https://');
}

/**
 * Cellule image d’un tableau : l’éditeur stocke souvent un raw Draft.js (JSON)
 * dont le texte du bloc est l’URL — pas une chaîne URL seule.
 */
function extractImageUrlFromTableCell(raw, variables) {
  if (raw == null || raw === '') return '';
  const asString = typeof raw === 'string' ? raw : String(raw);
  const trimmed = asString.trim();
  if (isImageUrl(trimmed) || isDataImageUrl(trimmed)) {
    return normalizeTableImageSrc(resolveVariables(trimmed, variables));
  }
  const fromDraft = draftToPlainText(asString).trim();
  if (!fromDraft) return '';
  const resolved = resolveVariables(fromDraft, variables).trim();
  if (isImageUrl(resolved) || isDataImageUrl(resolved)) {
    return normalizeTableImageSrc(resolved);
  }
  return '';
}

/**
 * Transforme une box en bloc Puck, ou retourne null si la box doit être ignorée.
 */
function boxToPuckBlock(box, configuration, variables, inlineStylesConfig, options = {}) {
  const { flowLayout = false, drawMode = false, hiddenTypes = EMPTY_HIDDEN_TYPES } = options;
  const type = box.type;

  if (IGNORED_TYPES.has(type)) return null;
  // Contenus que la société a choisi de ne pas reprendre sur ses sites
  // (règle générale `web.hidden_proposal_box_types`).
  if (hiddenTypes.has(type)) return null;
  if (dropsHiddenBox(box)) return null;

  // ── Texte & Titres ──
  if (TEXT_TYPES.has(type) || TITLE_TYPES.includes(type)) {
    const toColor = (value, fallback = null) => {
      if (!value) return fallback;
      if (typeof value === 'string') return value;
      return value.style || fallback;
    };

    const titleLevel = /^title([1-4])$/.test(type) ? Number(type.replace('title', '')) : null;
    const baseTextStyle = TEXT_TYPES.has(type)
      ? buildWebTextStyle(configuration, box.textStyleId, box, { drawMode })
      : (titleLevel
        ? buildWebTitleStyle(configuration, titleLevel, {
          drawMode,
          indexTitle: box.content?.indexTitle,
        })
        : null);
    const positions = resolveBoxPositions(box, baseTextStyle?.positions);

    const mergeBoxVisualStyle = (styleObj) => {
      const merged = { ...(styleObj || {}) };
      const boxBg = toColor(box.backgroundColor);
      if (boxBg && boxBg !== 'transparent' && boxBg !== 'rgba(255, 255, 255, 0.0)') {
        merged.backgroundColor = boxBg;
      }
      const boxBorderColor = toColor(box.border?.color);
      const boxBorderSize = Number(box.border?.size || 0);
      const hasBoxBorder = !!(
        boxBorderColor
        && boxBorderSize > 0
        && (box.border?.left || box.border?.right || box.border?.top || box.border?.bottom)
      );
      if (hasBoxBorder) {
        merged.borderColor = boxBorderColor;
        merged.borderWidth = drawMode ? boxBorderSize : (proposalPxToSpacingCss(boxBorderSize) || undefined);
      }
      return merged;
    };

    const draftToSiteHtml = (value) => resolveVariablesInHtml(draftToHtml(value, inlineStylesConfig), variables);
    const raw = box.content?.raw;
    if (!raw) return null;
    const html = draftToSiteHtml(raw);
    if (!html.trim()) return null;

    const commonProps = {
      id: `box-${box.id}`,
      html,
      textStyle: mergeBoxVisualStyle(baseTextStyle),
      ...(drawMode
        ? { drawMode: true }
        : {
          alignHorizontal: positions.horizontal,
          alignVertical: positions.vertical,
        }),
      isVisible: true,
      layoutTop: typeof box.top === 'number' ? box.top : 0,
    };

    // Un texte n'est pas un titre : bloc ProposalRichText (label « Texte »).
    // Historiquement tout partait en ProposalTitle (titleLevel null) → les
    // paragraphes s'affichaient « Titre » dans l'outline Puck.
    if (TEXT_TYPES.has(type)) {
      return { type: 'ProposalRichText', props: { ...commonProps, ...variableBoxFormProps(box, draftToSiteHtml) } };
    }

    return {
      type: 'ProposalTitle',
      props: {
        ...commonProps,
        titleLevel,
        indexTitle: Array.isArray(box.content?.indexTitle) ? box.content.indexTitle : null,
      },
    };
  }

  // ── Image ──
  if (IMAGE_TYPES.has(type)) {
    const hasImage = !!box.content?.src && isImageUrl(box.content.src);
    // Une image variable encore vide reste : le formulaire du site la remplira.
    if (!hasImage && type !== 'img_variable') return null;
    const src = hasImage ? box.content.src : '';
    const toColor = (value, fallback = null) => {
      if (!value) return fallback;
      if (typeof value === 'string') return value;
      return value.style || fallback;
    };
    const borderColor = toColor(box.border?.color);
    const borderWidth = Number(box.border?.size || 0);
    const hasBorder = !!(
      borderColor
      && borderWidth > 0
      && (box.border?.left || box.border?.right || box.border?.top || box.border?.bottom)
    );
    const positions = resolveBoxPositions(box, null);
    const boxWidth = typeof box.width === 'number' ? box.width : undefined;
    const boxHeight = typeof box.height === 'number' ? box.height : undefined;

    if (drawMode) {
      const rawH = box.positions?.horizontal;
      let imageAlign = 'center';
      if (rawH != null) {
        const h = String(rawH).toLowerCase();
        if (h === 'start' || h === 'left') imageAlign = 'left';
        else if (h === 'end' || h === 'right') imageAlign = 'right';
        else if (h === 'center' || h === 'middle') imageAlign = 'center';
      }
      return {
        type: 'ProposalImage',
        props: {
          id: `box-${box.id}`,
          src,
          alt: '',
          drawMode: true,
          alignment: imageAlign,
          maxWidth: boxWidth,
          width: boxWidth,
          height: boxHeight,
          backgroundColor: toColor(box.backgroundColor),
          borderColor: hasBorder ? borderColor : null,
          borderWidth: hasBorder ? borderWidth : 0,
          opacity: box.opacity,
          ...imageFitWebProps(box),
          ...variableBoxFormProps(box),
        },
      };
    }

    return {
      type: 'ProposalImage',
      props: {
        id: `box-${box.id}`,
        src,
        alt: '',
        // Position explicite du proposal respectée, sinon centre (règle :
        // toute image/vidéo est centrée par défaut).
        alignHorizontal: box.positions?.horizontal ?? 'center',
        alignVertical: positions.vertical,
        backgroundColor: toColor(box.backgroundColor),
        borderColor: hasBorder ? borderColor : null,
        borderWidth: hasBorder ? borderWidth : 0,
        opacity: box.opacity,
        // Contenir / remplir : cadre aux proportions de la box.
        ...imageFitWebProps(box),
        ...variableBoxFormProps(box),
      },
    };
  }

  // ── Volet ──
  // Box partagée : la box porte déjà les noms de props du bloc web, seuls les
  // deux textes et l'alignement sont traduits.
  if (type === ItemTypes.ACCORDION_PANEL) {
    const props = accordionPanelPropsFromBox(box, {
      toHtml: (raw) => resolveVariablesInHtml(draftToHtml(raw, inlineStylesConfig), variables),
      positions: resolveBoxPositions(box, null),
    });
    if (isEmptyAccordionPanelProps(props)) return null;
    return {
      type: 'AccordionPanel',
      props: { id: `box-${box.id}`, ...props },
    };
  }

  // ── Carte info ──
  // Même mécanique que le Volet : la box porte déjà les noms de props du bloc
  // web, seuls les deux textes et l'alignement sont traduits.
  if (type === ItemTypes.INFO_CARD) {
    const props = infoCardPropsFromBox(box, {
      toHtml: (raw) => resolveVariablesInHtml(draftToHtml(raw, inlineStylesConfig), variables),
      positions: resolveBoxPositions(box, null),
    });
    if (isEmptyInfoCardProps(props)) return null;
    return {
      type: 'InfoCard',
      props: { id: `box-${box.id}`, ...props },
    };
  }

  // ── Formes (rectangle / square / circle) ──
  if (type === 'shape') {
    const shape = box.content?.shape || 'rectangle';
    const color = box.content?.color || 'rgba(0,0,0,1)';
    const width = typeof box.width === 'number' ? box.width : undefined;
    const height = typeof box.height === 'number' ? box.height : undefined;
    const alignment = box.positions?.horizontal || 'left';

    return {
      type: 'ProposalShape',
      props: {
        id: `box-${box.id}`,
        shape,
        color,
        width,
        height,
        alignment,
        rotation: box.rotation || 0,
        borderColor: box.borders?.color?.style || null,
        borderWidth: Number(box.borders?.size || 0),
      },
    };
  }

  // ── Tableau ──
  if (TABLE_TYPES.has(type)) {
    return tableBoxToPuckBlock(box, configuration, variables, { drawMode });
  }

  // ── Cases à cocher (lecture seule) ──
  if (CHECKBOX_TYPES.has(type)) {
    return checkboxBoxToPuckBlock(box, inlineStylesConfig);
  }

  // ── Choix (lecture seule) ──
  if (type === 'selector_variable') {
    return selectorBoxToPuckBlock(box, inlineStylesConfig);
  }

  // Type inconnu → ignorer
  if (process.env.NODE_ENV === 'development') {
    console.warn(`[Parser] Type de box ignoré : "${type}" (id: ${box.id})`);
  }
  return null;
}

/**
 * Transforme une box en item absolu (pour les containers drawMode).
 * L'item contient soit un bloc proposal standard, soit une forme vectorielle.
 */
function boxToFreeLayoutItem(box, configuration, variables, inlineStylesConfig, hiddenTypes) {
  if (dropsHiddenBox(box)) return null;
  if (box.type === 'group_boxes') return null;
  if (hiddenTypes?.has(box.type)) return null;

  const left = typeof box.left === 'number' ? box.left : 0;
  const top = typeof box.top === 'number' ? box.top : 0;
  const width = typeof box.width === 'number' ? box.width : null;
  const height = typeof box.height === 'number' ? box.height : null;
  const clientHeight = typeof box.clientHeight === 'number' ? box.clientHeight : null;
  const effectiveHeight = box.type === 'text' && clientHeight && height
    ? Math.max(height, clientHeight)
    : (height ?? clientHeight);
  const zIndex = typeof box.zIndex === 'number' ? box.zIndex : 0;

  if (box.type === 'shape') {
    return {
      id: `item-${box.id}`,
      left,
      top,
      width,
      height: effectiveHeight,
      zIndex,
      kind: 'shape',
      shape: box.content?.shape || 'rectangle',
      color: box.content?.color || 'rgba(0,0,0,1)',
      rotation: box.rotation || 0,
      borderColor: box.borders?.color?.style || null,
      borderWidth: Number(box.borders?.size || 0),
      stroke: Number(box.stroke || 0),
      arrows: box.arrows || null,
    };
  }

  const block = boxToPuckBlock(box, configuration, variables, inlineStylesConfig, {
    drawMode: true,
    hiddenTypes,
  });
  if (!block) return null;

  return {
    id: `item-${box.id}`,
    left,
    top,
    width,
    height: effectiveHeight,
    zIndex,
    kind: 'block',
    block,
  };
}

// ─── 5.6 Tableaux ─────────────────────────────────────────────────────────────

/**
 * Même logique que TableHead (proposal) : les `width` des colonnes sont des poids
 * dont la somme fait la largeur du tableau ; on les convertit en % pour le site web.
 *
 * @param {Array<{ width?: number }>} cols
 * @returns {number[]} un pourcentage par colonne (somme ≈ 100)
 */
export function tableColumnsWidthPercent(cols) {
  if (!cols?.length) return [];
  const weights = cols.map((c) => {
    const w = c?.width;
    const n = typeof w === 'number' ? w : Number(w);
    return Number.isFinite(n) && n > 0 ? n : 0;
  });
  const total = weights.reduce((a, b) => a + b, 0);
  if (total <= 0) {
    const eq = 100 / cols.length;
    const equal = cols.map(() => Number(eq.toFixed(4)));
    const sumEq = equal.reduce((a, b) => a + b, 0);
    if (cols.length && Math.abs(sumEq - 100) > 1e-6) {
      equal[cols.length - 1] = Number((equal[cols.length - 1] + (100 - sumEq)).toFixed(4));
    }
    return equal;
  }
  const percents = weights.map((w) => Number(((w / total) * 100).toFixed(4)));
  const sum = percents.reduce((a, b) => a + b, 0);
  if (cols.length && Math.abs(sum - 100) > 1e-6) {
    percents[cols.length - 1] = Number((percents[cols.length - 1] + (100 - sum)).toFixed(4));
  }
  return percents;
}

/**
 * Transforme une box "table" en bloc EditableDataTable (Mantine, éditable dans Puck).
 */
export function tableBoxToPuckBlock(box, configuration, variables = {}, options = {}) {
  const { drawMode = false } = options;
  const { content, tableStyleId } = box;
  if (!content?.columns) return null;

  // Résoudre le style depuis configuration.tableStyles
  const design = proposalTableDesign(tableStyleId, configuration);

  const widthPercents = tableColumnsWidthPercent(content.columns);
  const positions = resolveBoxPositions(box, null);

  const columns = content.columns.map((col, i) => ({
    label: col.title || col.key,
    key: col.key,
    cellType: col.type === 'image' ? 'image' : 'text',
    textAlign: col.textAlign || 'start',
    verticalAlign: col.verticalAlign || 'center',
    widthPercent: widthPercents[i],
  }));

  // `rowKind` : type de ligne donné par l'API (zone), lu par les règles de
  // fusion et de style de `tableConfig` (cf. webTableMaterialize).
  const rows = (content.data || []).map((row) => ({
    ...(row.rowKind ? { rowKind: row.rowKind } : {}),
    cells: content.columns.map((col) => {
      const { key } = col;
      const isImage = col.type === 'image';
      const raw = row[key];
      let value = '';
      if (raw == null) {
        value = '';
      } else if (isImage) {
        value = extractImageUrlFromTableCell(raw, variables);
      } else {
        value = resolveVariables(String(raw), variables);
      }
      return {
        value,
        textAlign: row[`textAlign_${key}`] || '',
        verticalAlign: row[`verticalAlign_${key}`] || '',
      };
    }),
  }));

  return {
    type: 'EditableDataTable',
    props: {
      id: `box-${box.id}`,
      tableName: box.tableName || '',
      // Config du document (variante, colonnes, fusion, style des lignes) : le
      // site relit la table avec, et la rend comme le document.
      ...(box.tableConfig ? { tableConfig: toPuckTableConfig(box.tableConfig) } : {}),
      columns,
      rows,
      design,
      draftStyleContext: {
        colors: configuration?.colors || [],
        fontFamilyList: configuration?.fontFamilyList || [],
      },
      footerData: design.footer ? design.footerData : '',
      ...(drawMode
        ? { drawMode: true }
        : {}),
      ...variableBoxFormProps(box),
    },
  };
}

// ─── 5.7 Cases à cocher & Choix ───────────────────────────────────────────────

/**
 * Transforme une box "checkboxs_variable" en bloc ProposalCheckbox.
 */
export function checkboxBoxToPuckBlock(box, inlineStylesConfig = {}) {
  const question = draftToPlainText(box.name);
  const options = (box.checkboxs || []).map((cb) => ({
    label: draftToPlainText(cb.label),
    checked: !!cb.checked,
  }));

  if (!question && options.length === 0) return null;

  return {
    type: 'ProposalCheckbox',
    props: {
      id: `box-${box.id}`,
      question,
      options,
      checkboxStyleId: box.checkboxStyleId || null,
      checkboxStyles: inlineStylesConfig?.checkboxStyles || [],
      fontFamilyList: inlineStylesConfig?.fontFamilyList || [],
    },
  };
}

/**
 * Transforme une box « Choix » (`selector_variable`) en bloc ProposalSelector.
 *
 * Les libellés sont du raw Draft.js, comme ceux des cases à cocher (voir
 * `textToRaw`, `utils/dataSync.js`). Fidèle au rendu du Proposal
 * (`common/SelectorVariable.jsx:132-190`) : **seules les options retenues**
 * s'affichent, jointes par le séparateur du style, et l'intitulé de la variable
 * (`box.name`) ne fait pas partie de la box — il vit dans le formulaire.
 * Un choix sans réponse ne produit donc aucun bloc.
 */
export function selectorBoxToPuckBlock(box, inlineStylesConfig = {}) {
  const options = (box.options || [])
    .filter((option) => option?.selected)
    .map((option) => draftToPlainText(option.label))
    .filter(Boolean);

  if (options.length === 0) return null;

  return {
    type: 'ProposalSelector',
    props: {
      id: `box-${box.id}`,
      options,
      selectorStyleId: box.selectorStyleId ?? null,
      selectorStyles: inlineStylesConfig?.selectorStyles || [],
    },
  };
}

// ─── 5.5 Containers → ProposalSection ────────────────────────────────────────

/**
 * Transforme un container (et ses colonnes/boxes) en bloc ProposalSection Puck.
 * Retourne null si le container ne contient aucun bloc visible.
 */
/** @returns {{ block: object, zones: Record<string, Array<object>> } | null} */
function containerToPuckBlock(
  container,
  columns,
  allBoxes,
  configuration,
  variables,
  inlineStylesConfig,
  { marginBottom = '', hiddenTypes = EMPTY_HIDDEN_TYPES, rowContainers = [] } = {},
) {
  // Important : certaines sections utiles du proposal sont en drawMode/maskId,
  // donc on ne les exclut pas ici. Le filtrage se fait ensuite sur les blocs
  // réellement convertibles (boxToPuckBlock + hasContent).
  const layout = resolveContainerLayout({
    container,
    columns,
    rowContainers,
    boxesOf: (columnId) => allBoxes
      .filter((b) => b.columnId === columnId && !dropsHiddenBox(b))
      .sort((a, b) => (a.top ?? 0) - (b.top ?? 0)),
  });

  if (!layout) return null;

  const sectionId = `section-${container.id}`;
  const zones = {};

  layout.cells.forEach((cell) => {
    zones[zoneKeyForSectionColumn(sectionId, cell.id)] = cell.boxes
      .map((box) => boxToPuckBlock(box, configuration, variables, inlineStylesConfig, {
        flowLayout: true,
        hiddenTypes,
      }))
      .filter(Boolean);
  });

  const hasContent = Object.values(zones).some((arr) => arr.length > 0);
  if (!hasContent) return null;

  return {
    block: {
      type: 'ProposalSection',
      props: {
        id: sectionId,
        backgroundColor: '',
        paddingTop: 0,
        paddingBottom: 0,
        marginBottom: marginBottom || '',
        isVisible: true,
        // Ligne bloquée dans le template classique : le Bloc reste figé sur le site.
        ...(container.editable === false ? { locked: true } : {}),
        sectionColumns: layout.preset === 'nested'
          ? { preset: 'nested', nested: layout.nested }
          : {
            preset: 'row',
            columnIds: layout.columnIds,
            columnSpans: layout.columnSpans,
          },
      },
    },
    zones,
  };
}

/**
 * Transforme un container drawMode en bloc ProposalFreeLayout.
 * Les boxes sont positionnées en absolu (left/top/width/height/zIndex).
 */
function containerToFreeLayoutBlock(
  container,
  columns,
  allBoxes,
  configuration,
  variables,
  inlineStylesConfig,
  { marginBottom = '', hiddenTypes = EMPTY_HIDDEN_TYPES } = {},
) {
  const containerColumns = container.columnsIds
    .map((colId) => columns.find((c) => c.id === colId))
    .filter(Boolean);

  if (containerColumns.length === 0) return null;

  const items = containerColumns
    .flatMap((col) => allBoxes.filter((b) => b.columnId === col.id && !dropsHiddenBox(b)))
    .sort((a, b) => {
      const topA = a.top ?? 0;
      const topB = b.top ?? 0;
      if (topA !== topB) return topA - topB;
      return (a.zIndex ?? 0) - (b.zIndex ?? 0);
    })
    .map((box) => boxToFreeLayoutItem(box, configuration, variables, inlineStylesConfig, hiddenTypes))
    .filter(Boolean);

  if (items.length === 0) return null;

  const computedHeight = items.reduce((max, item) => {
    const h = typeof item.height === 'number' ? item.height : 0;
    return Math.max(max, item.top + h);
  }, 0);

  const containerHeight = typeof container.height === 'number' && container.height > 0
    ? container.height
    : computedHeight;

  return {
    type: 'ProposalFreeLayout',
    props: {
      id: `section-${container.id}`,
      canvasWidth: PAGE_WIDTH_PX,
      minHeight: Math.max(120, Math.ceil(containerHeight)),
      marginBottom: marginBottom || '',
      items,
    },
  };
}

// ─── 5.9 Extraction des styles du proposal ───────────────────────────────────

export { extractProposalStyles };

// ─── 5.8 Fonction principale ──────────────────────────────────────────────────

/**
 * Parse un proposal complet et retourne la structure web_data.
 *
 * @param {object} proposalContent - L'objet complet du proposal (boxes, containers, columns, configuration)
 * @param {object} webTemplate - Le template web choisi par l'utilisateur (pour le thème)
 * @param {object} resolvedVariables - Les variables résolues (opportunity.X, custom.X...)
 * @param {object} [rules] - Règles générales de la société, résolues
 * @param {Set<string>} [rules.hiddenBoxTypes] - types de box à ne pas reprendre
 * @param {boolean} [rules.chapterSeparationTitleOnly] - réduire une séparation
 *        de chapitre à son seul titre, quel que soit le réglage du document
 * @returns {object} web_data { projectConfig, pages, pageOrder, skeleton, compositions }
 */
export function parseProposalToWebData(
  proposalContent,
  webTemplate = {},
  resolvedVariables = {},
  rules = {},
) {
  const {
    boxes = [],
    containers = [],
    columns = [],
    masks = [],
    rowContainers = [],
    configuration = {},
  } = proposalContent;
  const {
    hiddenBoxTypes = EMPTY_HIDDEN_TYPES,
    chapterSeparationTitleOnly = false,
  } = rules;

  // Récupérer les styles inline depuis la config.
  // On passe toute la configuration pour reconstruire les styles Draft
  // (colors/fontFamilyList/FONT_SIZE_xx) même si inlineStyles est absent.
  const inlineStylesConfig = {
    ...(configuration || {}),
    ...(configuration.inlineStyles || {}),
  };

  // ── Séparations de chapitre ──
  // Une séparation produit deux containers — celui du masque et celui de son
  // titre lié — dont un seul s'affiche. Le document tranche par
  // `displayChapterSeparation` ; la règle générale impose le titre seul, un
  // masque pleine page rendant mal sur un site. Le filtre tourne dans les deux
  // cas : sans lui les deux containers convertissent, et chacun ouvrirait sa
  // propre page.
  const displayChapterSeparation = chapterSeparationTitleOnly
    ? false
    : (configuration.displayChapterSeparation ?? true);
  const visibleContainers = filterChapterSeparationContainers({
    containers, boxes, columns, masks, displayChapterSeparation,
  });

  // Index rapide pour les lookups
  const columnById = Object.fromEntries(columns.map((c) => [c.id, c]));
  const containerById = Object.fromEntries(visibleContainers.map((c) => [c.id, c]));

  // ── Découpage en pages ──
  const pageDefinitions = splitIntoPages(boxes, visibleContainers, columns, rowContainers);

  /** De quoi descendre dans les lignes imbriquées d'un container. */
  const layoutTree = { columns, rowContainers };

  const reserveUniqueSlug = makeReserveUniqueSlug();

  // ── Pages statiques du template (ordre = static_page_order) ──
  const { staticPages, staticSlugs } = mapTemplateStaticPages(webTemplate, reserveUniqueSlug);

  // ── Génération des pages proposal ──
  const proposalPages = {};

  pageDefinitions.forEach((pageDef) => {
    const slug = reserveUniqueSlug(pageDef.slug || pageDef.title);

    // Transformer les containers de cette page en blocs Puck (+ zones DropZone)
    const mergedZones = {};
    let pendingSpacingPx = 0;
    const content = pageDef.containerIds
      .map((cId) => containerById[cId])
      .filter(Boolean)
      .map((container) => {
        if (isSpacingOnlyContainer(container, boxes, layoutTree)) {
          pendingSpacingPx += computeContainerAllSpacingPx(
            container, boxes, configuration, layoutTree,
          );
          return null;
        }

        const marginPx = pendingSpacingPx
          + computeContainerTrailingSpacingPx(container, boxes, configuration, layoutTree);
        pendingSpacingPx = 0;
        const marginBottom = computeSectionMarginBottomCss(configuration, {
          trailingSpacingPx: marginPx,
        });

        const hasDrawModeColumn = (container.columnsIds || []).some((colId) => {
          const col = columns.find((c) => c.id === colId);
          return !!col?.drawMode;
        });

        if (hasDrawModeColumn) {
          return containerToFreeLayoutBlock(
            container,
            columns,
            boxes,
            configuration,
            resolvedVariables,
            inlineStylesConfig,
            { marginBottom, hiddenTypes: hiddenBoxTypes },
          );
        }

        const sectionPack = containerToPuckBlock(
          container,
          columns,
          boxes,
          configuration,
          resolvedVariables,
          inlineStylesConfig,
          { marginBottom, hiddenTypes: hiddenBoxTypes, rowContainers },
        );
        if (!sectionPack) return null;
        Object.assign(mergedZones, sectionPack.zones);
        return sectionPack.block;
      })
      .filter(Boolean);

    proposalPages[slug] = {
      title: pageDef.title,
      slug,
      type: pageDef.type,
      puckData: {
        content,
        root: {},
        ...(Object.keys(mergedZones).length ? { zones: mergedZones } : {}),
      },
    };
  });

  // Onglets : pages statiques en premier, puis pages proposal (ordre d'apparition des title1).
  const pages = { ...staticPages, ...proposalPages };
  const pageOrder = [...staticSlugs, ...Object.keys(proposalPages)];

  // ── Config du projet ──
  const theme = webTemplate.theme || {};
  const proposalStyles = extractProposalStyles(configuration);
  const projectConfig = {
    clientName: resolvedVariables['opportunity.OFFER_NAME'] || '',
    webTemplateId: webTemplate.id || null,
    theme: {
      primaryColor: theme.primaryColor || configuration.primaryColor || '#228be6',
      fontFamily: configuration.fontFamily || DEFAULT_SITE_FONT,
      headingColor: theme.headingColor || '',
      logoUrl: webTemplate.logoUrl || '',
    },
    proposalStyles,
    indentationTitles: configuration.indentationTitles ?? 0,
    proposalContentWidthPx: computeProposalContentWidthPx(configuration),
    margins: configuration.margins || null,
    variables: resolvedVariables,
    /** Couleurs proposal (hex uniques) pour Tiptap dans le web-editor */
    richTextPalette: extractProposalRichTextPalette(configuration),
  };

  // ── Squelette du site ──
  // Copié depuis le template, pas référencé : à partir d'ici le document fait
  // foi et se modifie section par section (le template n'est qu'un modèle).
  const skeleton = buildSkeletonFromTemplate(webTemplate);

  // ── Compositions du modèle ──
  // Copiées elles aussi : le document part avec la bibliothèque de son modèle,
  // puis vit sa vie — en enregistrer une ici ne remonte pas au modèle, et le
  // modèle qui change n'en retire aucune de ce document.
  const compositions = webTemplate.compositions && typeof webTemplate.compositions === 'object'
    ? webTemplate.compositions
    : {};

  return {
    projectConfig, pages, pageOrder, skeleton, compositions,
  };
}
